import React, { useRef } from "react";
import TranscriptPanel, { TranscriptMessage } from "./components/TranscriptPanel";
import { useStageAnchorCentering } from "./useStageAnchorCentering";

interface LiveAgentContainerProps {
  children: React.ReactNode;
  showTranscript: boolean;
  isMobile: boolean;
  brandColor: string;
  agentName?: string;
  messages: TranscriptMessage[];
  onCloseTranscript: () => void;
  leftWidthPx?: number;      // fixed width for avatar column (desktop)
  transcriptWidthPx?: number; // fixed width for transcript column (desktop)
  gapPx?: number;            // column gap (desktop)
  isConnected?: boolean;
  isListening?: boolean;
  onSendMessage?: (text: string) => void;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

export const LiveAgentContainer: React.FC<LiveAgentContainerProps> = ({
  children,
  showTranscript,
  isMobile,
  brandColor,
  agentName,
  messages,
  onCloseTranscript,
  leftWidthPx = 600,
  transcriptWidthPx = 440,
  gapPx = 12,
  isConnected = false,
  isListening = false,
  onSendMessage,
  isMuted = false,
  onToggleMute,
}) => {
  // Hold the genie at the centre of the screen however long the status text
  // above it or the controls below it are.
  const containerRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const anchorOffset = useStageAnchorCentering(containerRef, columnRef, !isMobile);

  if (isMobile) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: "10px",
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        padding: "20px",
      }}
    >
      {(() => {
        const leftWidth = leftWidthPx;
        const panelWidth = transcriptWidthPx;
        const gap = gapPx;
        const gridTemplate = showTranscript ? `${leftWidth}px ${panelWidth}px` : `${leftWidth}px`;
        const maxWidth = showTranscript ? leftWidth + gap + panelWidth : leftWidth;
        return (
      <div
        style={{
          width: "100%",
          maxWidth,
          display: "grid",
          gridTemplateColumns: gridTemplate,
          columnGap: gap,
          alignItems: "center",
        }}
      >
        <div
          ref={columnRef}
          className="motion-safe:transition-[top] motion-safe:duration-300 motion-safe:ease-out"
          style={{
            position: "relative",
            top: anchorOffset,
            width: leftWidth,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
        {showTranscript && (
          <TranscriptPanel
            open
            brandColor={brandColor}
            agentName={agentName}
            messages={messages}
            onClose={onCloseTranscript}
            width={panelWidth}
            maxHeightVh={70}
            insetRightVw={0}
            isConnected={isConnected}
            isListening={isListening}
            onSendMessage={onSendMessage}
            isMuted={isMuted}
            onToggleMute={onToggleMute}
          />
        )}
      </div>
        );
      })()}
    </div>
  );
};

export default LiveAgentContainer;


